'use client';

import { AUTO_REPLIES_COPY, AUTO_REPLY_DEFAULT_SCENARIO_NAME } from '@/components/auto-replies-copy';
import { UIPriority } from '@/enums/ui-priorities';
import Button from '@/ui/Button';
import clsx from 'clsx';
import type { DragEvent, KeyboardEvent, MouseEvent } from 'react';

type AutoReplyScenarioHeaderProps = {
    scenarioId: string;
    position: number;
    name: string;
    enabled: boolean;
    expanded: boolean;
    disabled: boolean;
    onToggleExpanded: () => void;
    onToggleEnabled: () => void;
    onDragStart: (event: DragEvent<HTMLButtonElement>) => void;
    onDragEnd: () => void;
};

const stopHeaderClickPropagation = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
};

export const AutoReplyScenarioHeader = ({
    scenarioId,
    position,
    name,
    enabled,
    expanded,
    disabled,
    onToggleExpanded,
    onToggleEnabled,
    onDragStart,
    onDragEnd,
}: AutoReplyScenarioHeaderProps) => {
    const scenarioName = name || AUTO_REPLY_DEFAULT_SCENARIO_NAME;
    const disclosureLabel = expanded
        ? AUTO_REPLIES_COPY.collapseScenario(scenarioName)
        : AUTO_REPLIES_COPY.expandScenario(scenarioName);

    const onHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        onToggleExpanded();
    };

    return (
        <div
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-controls={`auto-reply-scenario-body-${scenarioId}`}
            aria-label={disclosureLabel}
            data-testid={`auto-reply-scenario-header-${scenarioId}`}
            className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={onToggleExpanded}
            onKeyDown={onHeaderKeyDown}
        >
            <button
                type="button"
                draggable={!disabled}
                aria-label={AUTO_REPLIES_COPY.dragScenario(position)}
                title="Перетащить"
                className="flex h-7 w-7 shrink-0 cursor-grab items-center justify-center rounded border border-slate-200 bg-white text-sm font-semibold text-slate-500 active:cursor-grabbing disabled:cursor-default disabled:bg-slate-100"
                disabled={disabled}
                onClick={stopHeaderClickPropagation}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
            >
                ↕
            </button>
            <span className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <span className="w-10 shrink-0 text-sm font-semibold text-slate-600">#{position}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{scenarioName}</span>
            </span>
            <span
                data-testid={`auto-reply-scenario-status-action-${scenarioId}`}
                className="ml-auto flex shrink-0 items-center gap-2"
            >
                <Button
                    type="button"
                    priorityType={UIPriority.SECONDARY}
                    uiSize="compact"
                    className="!w-[88px] shrink-0 whitespace-nowrap px-2"
                    onClick={(event) => {
                        event.stopPropagation();
                        onToggleEnabled();
                    }}
                >
                    {enabled ? AUTO_REPLIES_COPY.disableScenario : AUTO_REPLIES_COPY.enableScenario}
                </Button>
                <span
                    className={clsx(
                        'shrink-0 rounded px-2 py-0.5 text-xs',
                        enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500',
                    )}
                >
                    {enabled ? AUTO_REPLIES_COPY.scenarioEnabled : AUTO_REPLIES_COPY.scenarioDisabled}
                </span>
            </span>
        </div>
    );
};
